/**
 * Schema definitions for Project Services domain
 * These schemas define the shape of data structures used throughout the feature
 */

// Crew-related schemas
export const CrewSchema = {
  id: null,
  crew_number: null,
  active: true,
  users: [],
  customers: [],
  created_at: null,
  updated_at: null,
};

export const CrewMemberSchema = {
  id: null,
  first_name: '',
  last_name: '',
  email: '',
  customer: null,
  is_active: true,
};

export const CrewExtraPaySchema = {
  id: null,
  member: null,
  extra_pay: 0.0,
  crew: null,
  created_at: null,
  updated_at: null,
};

// Service-related schemas
export const JobServiceSchema = {
  id: null,
  title: '',
  name: '',
  service_type: 'site', // 'site' | 'panel'
  target_field: '',
  description: '',
  active: true,
  created_at: null,
  updated_at: null,
};

export const ServicePerformedSchema = {
  id: null,
  service: null,
  service_id: null,
  crew: null,
  crew_id: null,
  job: null,
  job_id: null,
  site: null,
  site_id: null,
  relay_load: null,
  service_date: null,
  price_paid_to_crew: 0.0,
  extra_paid_to_crew: 0.0,
  price_charged_to_customer: 0.0,
  notes: '',
  created_at: null,
  updated_at: null,
};

// Project Job schemas
export const ProjectJobSchema = {
  id: null,
  site: null,
  site_id: null,
  crew: null,
  crew_id: null,
  installation_date: null,
  submitted_by: null,
  submitted_by_id: null,
  approved_by: null,
  approved_by_id: null,
  notes: '',
  specifics: {},
  pending: false,
  services_performed: [],
  panel_job_info: [],
  created_at: null,
  updated_at: null,
};

export const ProjectJobLoadInformationSchema = {
  id: null,
  job: null,
  job_id: null,
  relay_load: null,
  relay_load_id: null,
  installation_service: null,
  installation_service_id: null,
  panel_specifics: {},
  created_at: null,
  updated_at: null,
};

// Customer Job Configuration schemas
export const CustomerJobServicePricesSchema = {
  id: null,
  customer: null,
  customer_id: null,
  service: null,
  service_id: null,
  installer_pay: 0.0,
  customer_charge: 0.0,
  account_extra_pay: false,
  active: true,
  created_at: null,
  updated_at: null,
};

export const CustomerJobConfigSchema = {
  id: null,
  customer: null,
  customer_id: null,
  configuration: {},
  created_at: null,
  updated_at: null,
};

// Plant Data Import schemas
export const PlantDataImportSchema = {
  id: null,
  import_data: {},
  status: 'pending', // 'pending' | 'confirmed' | 'rejected'
  site: null,
  site_id: null,
  imported_by: null,
  imported_by_id: null,
  confirmed_by: null,
  confirmed_by_id: null,
  created_at: null,
  updated_at: null,
};

// Aggregation schemas for reporting
export const ServiceAggregationByDateSchema = {
  service_date: null,
  site_group: null,
  total_price_paid_to_crew: 0.0,
  total_extra_paid_to_crew: 0.0,
  total_price_charged_to_customer: 0.0,
  profit: 0.0,
  quantity: 0,
};

export const ServiceAggregationByServiceSchema = {
  service: null,
  total_price_paid_to_crew: 0.0,
  total_extra_paid_to_crew: 0.0,
  total_price_charged_to_customer: 0.0,
  profit: 0.0,
  quantity: 0,
};

export const ServiceAggregationByCrewServiceSchema = {
  crew: null,
  service: null,
  total_price_paid_to_crew: 0.0,
  total_extra_paid_to_crew: 0.0,
  total_price_charged_to_customer: 0.0,
  profit: 0.0,
  quantity: 0,
};

// Default values and utility functions
export const getDefaultJobSpecifics = () => ({
  // Site-level service flags
  meter_set: false,
  meter_change: false,
  service_upgrade: false,
  transformer_change: false,
  // Add other default specifics as needed
});

export const getDefaultJobPanelSpecifics = () => ({
  // Panel-level service flags
  panel_installed: false,
  panel_upgraded: false,
  // Add other default panel specifics as needed
});

export const getDefaultJobConfig = () => ({
  // Default job configuration settings
  require_photos: true,
  require_signature: true,
  auto_calculate_services: true,
  // Add other default configuration options
});

// Validation helpers
export const validateCrewNumber = crewNumber => {
  return crewNumber && Number.isInteger(Number(crewNumber)) && Number(crewNumber) > 0;
};

export const validateServicePrice = price => {
  return price !== null && price !== undefined && Number(price) >= 0;
};

export const validateServiceDate = date => {
  return date && !isNaN(Date.parse(date));
};

// Status constants
export const SERVICE_TYPES = {
  SITE: 'site',
  PANEL: 'panel',
};

export const PLANT_IMPORT_STATUS = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  REJECTED: 'rejected',
};

export const JOB_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  COMPLETED: 'completed',
};

// Entity relationship helpers
export const createEmptyCrew = () => ({ ...CrewSchema });
export const createEmptyJobService = () => ({ ...JobServiceSchema });
export const createEmptyProjectJob = () => ({ ...ProjectJobSchema });
export const createEmptyServicePerformed = () => ({ ...ServicePerformedSchema });
export const createEmptyCrewExtraPay = () => ({ ...CrewExtraPaySchema });
export const createEmptyPlantDataImport = () => ({ ...PlantDataImportSchema });
